import React from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Card,
    CardContent,
    Stack,
    Chip,
    Divider,
    Alert,
    Grid
} from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Refresh,
    EmojiEvents,
    TrendingUp,
    ArrowForward
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { StaticQuizDTO, QuizAttemptDTO, QuizQuestionDTO } from '../../services/quizService';

interface QuizResultsProps {
    quiz: StaticQuizDTO;
    attempt: QuizAttemptDTO;
    onRetry: () => void;
    onTryAnother?: () => void;
    hasMoreQuizzes?: boolean;
}

interface QuestionResult {
    question: QuizQuestionDTO;
    userAnswer: any;
    correctAnswer: any;
    isCorrect: boolean;
    explanation?: string;
}

// Helper to format answers for display (used by both QuizResults and QuestionReviewCard)
const formatAnswer = (answer: any, questionType: string): string => {
    if (answer === null || answer === undefined) return 'Not answered';
    
    if (Array.isArray(answer)) {
        return answer.join(', ');
    }
    
    if (typeof answer === 'object') {
        // For matching/drag-drop, format as key-value pairs
        const pairs = Object.entries(answer).map(([key, value]) => `${key} → ${value}`);
        return pairs.join(' | ');
    }
    
    return String(answer);
};

const QuizResults: React.FC<QuizResultsProps> = ({ 
    quiz, 
    attempt, 
    onRetry, 
    onTryAnother,
    hasMoreQuizzes = false 
}) => {
    const maxScore = quiz.questions.reduce((acc, q) => acc + (q.points || 1), 0);
    // attempt.score from QuizRunner is actual score points (not percentage)
    const scorePoints = typeof attempt.score === 'number' ? attempt.score : 0;
    // Calculate percentage from actual score points
    const percentage = maxScore > 0 ? Math.round((scorePoints / maxScore) * 100) : 0;
    const minPassPercent = quiz.minPassPercent || 70;
    const passed = percentage >= minPassPercent;

    // Helper function to check if answer is correct (same as QuizRunner)
    const isAnswerCorrect = (question: any, userAnswer: any): boolean => {
        if (!userAnswer) return false;

        switch (question.type) {
            case 'MCQ':
            case 'TRUE_FALSE':
            case 'SHORT_ANSWER':
                return String(userAnswer).toLowerCase().trim() === String(question.correctAnswer).toLowerCase().trim();
            
            case 'NUMERIC':
                const correctNum = typeof question.correctAnswer === 'number' ? question.correctAnswer : parseFloat(question.correctAnswer);
                const userNum = typeof userAnswer === 'number' ? userAnswer : parseFloat(userAnswer);
                const tolerance = (Array.isArray(question.options) && question.options[0]?.tolerance) || 0;
                return Math.abs(userNum - correctNum) <= tolerance;
            
            case 'MULTIPLE_SELECT':
                const correctArray = Array.isArray(question.correctAnswer) ? question.correctAnswer.sort() : [];
                const userArray = Array.isArray(userAnswer) ? userAnswer.sort() : [];
                return JSON.stringify(correctArray) === JSON.stringify(userArray);
            
            case 'ORDERING':
                const correctOrder = Array.isArray(question.correctAnswer) ? question.correctAnswer : [];
                const userOrder = Array.isArray(userAnswer) ? userAnswer : [];
                return JSON.stringify(correctOrder) === JSON.stringify(userOrder);
            
            case 'MATCHING':
            case 'DRAG_DROP':
                const correctMapping = typeof question.correctAnswer === 'object' ? question.correctAnswer : {};
                const userMapping = typeof userAnswer === 'object' ? userAnswer : {};
                return JSON.stringify(correctMapping) === JSON.stringify(userMapping);
            
            case 'FILL_BLANK':
                const correctBlanks = typeof question.correctAnswer === 'object' ? question.correctAnswer : {};
                const userBlanks = typeof userAnswer === 'object' ? userAnswer : {};
                return JSON.stringify(correctBlanks) === JSON.stringify(userBlanks);
            
            default:
                return String(userAnswer) === String(question.correctAnswer);
        }
    };

    // Calculate question results
    const questionResults: QuestionResult[] = quiz.questions.map(q => {
        const userAnswer = attempt.answers[q.id];
        const isCorrect = isAnswerCorrect(q, userAnswer);
        
        return {
            question: q,
            userAnswer: userAnswer || 'Not answered',
            correctAnswer: q.correctAnswer,
            isCorrect,
            explanation: q.explanation
        };
    });

    const correctCount = questionResults.filter(r => r.isCorrect).length;
    const incorrectResults = questionResults.filter(r => !r.isCorrect);

    return (
        <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
            {/* Score Summary */}
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    mb: 4,
                    textAlign: 'center',
                    background: passed
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white'
                }}
            >
                <Stack spacing={2} alignItems="center">
                    {passed ? (
                        <EmojiEvents sx={{ fontSize: 80 }} />
                    ) : (
                        <TrendingUp sx={{ fontSize: 80 }} />
                    )}
                    
                    <Typography variant="h3" fontWeight="bold">
                        {percentage}%
                    </Typography>
                    
                    <Chip
                        label={passed ? 'PASSED' : 'FAILED'}
                        color={passed ? 'success' : 'error'}
                        sx={{
                            bgcolor: 'white',
                            color: passed ? 'success.main' : 'error.main',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            px: 2,
                            py: 0.5
                        }}
                    />
                    
                    <Typography variant="h6">
                        {correctCount} out of {quiz.questions.length} correct
                    </Typography>
                    
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        Pass mark: {minPassPercent}%
                    </Typography>
                </Stack>
            </Paper>

            {/* Statistics */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Score
                            </Typography>
                            <Typography variant="h4" fontWeight="bold">
                                {scorePoints}/{maxScore}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Correct Answers
                            </Typography>
                            <Typography variant="h4" fontWeight="bold" color="success.main">
                                {correctCount}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Incorrect Answers
                            </Typography>
                            <Typography variant="h4" fontWeight="bold" color="error.main">
                                {quiz.questions.length - correctCount}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                    variant="outlined"
                    size="large"
                    startIcon={<Refresh />}
                    onClick={onRetry}
                >
                    Retake This Quiz
                </Button>
                {hasMoreQuizzes && onTryAnother && (
                    <Button
                        variant="contained"
                        size="large"
                        color="primary"
                        startIcon={<ArrowForward />}
                        onClick={onTryAnother}
                    >
                        Try Another Quiz
                    </Button>
                )}
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Review Section */}
            <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Review Your Answers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Review all questions and see the correct answers for questions you missed.
                </Typography>

                {/* Incorrect Answers First */}
                {incorrectResults.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Questions to Review ({incorrectResults.length})
                            </Typography>
                        </Alert>
                        {incorrectResults.map((result, index) => (
                            <QuestionReviewCard
                                key={result.question.id}
                                index={questionResults.findIndex(r => r.question.id === result.question.id)}
                                result={result}
                                isIncorrect={true}
                            />
                        ))}
                    </Box>
                )}

                {/* All Questions */}
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
                    All Questions
                </Typography>
                <Stack spacing={3}>
                    {questionResults.map((result, index) => (
                        <QuestionReviewCard
                            key={result.question.id}
                            index={index}
                            result={result}
                            isIncorrect={!result.isCorrect}
                        />
                    ))}
                </Stack>
            </Box>
        </Box>
    );
};

interface QuestionReviewCardProps {
    index: number;
    result: QuestionResult;
    isIncorrect: boolean;
}

const QuestionReviewCard: React.FC<QuestionReviewCardProps> = ({ index, result, isIncorrect }) => {
    const { question, userAnswer, correctAnswer, explanation } = result;

    return (
        <Card
            variant="outlined"
            sx={{
                borderLeft: isIncorrect ? '4px solid' : '4px solid',
                borderLeftColor: isIncorrect ? 'error.main' : 'success.main',
                bgcolor: isIncorrect ? 'error.50' : 'success.50'
            }}
        >
            <CardContent>
                <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                    <Chip
                        icon={isIncorrect ? <Cancel /> : <CheckCircle />}
                        label={`Question ${index + 1}`}
                        color={isIncorrect ? 'error' : 'success'}
                        sx={{ minWidth: 120 }}
                    />
                    <Chip
                        label={question.type.replace('_', ' ')}
                        size="small"
                        variant="outlined"
                    />
                </Box>

                <Box sx={{ mb: 3, '& img': { maxWidth: '100%' } }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {question.content}
                    </ReactMarkdown>
                </Box>

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: isIncorrect ? 6 : 12 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                bgcolor: isIncorrect ? 'error.light' : 'success.light',
                                border: `2px solid ${isIncorrect ? 'error.main' : 'success.main'}`
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                Your Answer:
                            </Typography>
                            <Typography variant="body1">
                                {formatAnswer(userAnswer, question.type)}
                            </Typography>
                        </Paper>
                    </Grid>
                    {isIncorrect && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    bgcolor: 'success.light',
                                    border: '2px solid',
                                    borderColor: 'success.main'
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                    Correct Answer:
                                </Typography>
                                <Typography variant="body1">
                                    {formatAnswer(correctAnswer, question.type)}
                                </Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>

                {explanation && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Explanation:
                        </Typography>
                        <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {explanation}
                        </ReactMarkdown>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default QuizResults;
